// Auto-generated from OpenAPI schema — do not edit manually.
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ApiError,
  apiClient,
  invalidateEntity,
  invalidateEntityDetail,
  type AllocationCreate,
  type Announcement,
  type AnnouncementStatsResponse,
  type AssignPathways,
  type AssignPathwaysResponse,
  type BulkAllocation,
  type BulkAllocationResponse,
  type BulkAllocationValidationResponse,
  type BulkCSVFile,
  type BulkEnroll,
  type BulkEnrollResponse,
  type BulkOperation,
  type BulkUpload,
  type BulkUploadResult,
  type BulkValidationResponse,
  type CertificateVerify,
  type ChangePassword,
  type ChangePasswordSuccess,
  type CompanyCreate,
  type CompanyDetail,
  type CompanyLearnerDashboardStats,
  type CompanyMFAPolicy,
  type CompanySubscription,
  type CourseAllocation,
  type CourseCreateRequest,
  type CourseCreateResponse,
  type CourseDetailResponse,
  type CourseImageUploadRequest,
  type CourseImageUploadResponse,
  type CourseLogoUploadRequest,
  type CourseLogoUploadResponse,
  type CourseMetadata,
  type CoursePrerequisite,
  type CoursePurchaseRequest,
  type CoursePurchaseResponse,
  type CourseRequest,
  type CourseRequestCreate,
  type CourseRequestReview,
  type CreateIndividualLearner,
  type CreateLearner,
  type CreditTransaction,
  type DeactivateCourseResponse,
  type EnrollLearner,
  type EnrollmentStatusResponse,
  type EnterpriseLearner,
  type EnterpriseLearnerDetail,
  type EnterpriseUser,
  type HandbookUploadRequest,
  type HandbookUploadResponse,
  type IndividualLearner,
  type IndividualPurchase,
  type InviteUser,
  type LearnerDashboardStats,
  type LearnerNote,
  type LearnerProgress,
  type LearningEvent,
  type LorCreateRequest,
  type LorCreateResponse,
  type MoveMember,
  type PaginatedAnnouncementListList,
  type PaginatedCatalogCourseResponse,
  type PaginatedCompanyListList,
  type PaginatedCompanySubscriptionList,
  type PaginatedCourseAllocationList,
  type PaginatedCourseRequestList,
  type PaginatedCreditTransactionList,
  type PaginatedEnterpriseLearnerList,
  type PaginatedEnterpriseUserList,
  type PaginatedIndividualLearnerList,
  type PaginatedLearnerCourseResponse,
  type PaginatedLearnerNoteList,
  type PaginatedLearnerProgressList,
  type PaginatedLearningEventList,
  type PaginatedPathwayCourseResponse,
  type PaginatedPathwayEnrollmentList,
  type PaginatedPathwayList,
  type PaginatedReportDefinitionList,
  type PaginatedReportRunList,
  type PaginatedSectorList,
  type PaginatedTeamList,
  type PaginatedUserListList,
  type PaginatedUserRoleList,
  type PatchedAnnouncement,
  type PatchedCompanyMFAPolicy,
  type PatchedCompanyUpdate,
  type PatchedEnterpriseUser,
  type PatchedLearnerNote,
  type PatchedPathway,
  type PatchedPathwayCourseUpdate,
  type PatchedReportDefinition,
  type PatchedSSOConfigUpdate,
  type PatchedTeam,
  type PatchedUpdateIndividualLearner,
  type PatchedUpdateLearner,
  type PatchedUserRole,
  type Pathway,
  type PathwayAllocation,
  type PathwayAllocationResponse,
  type PathwayCourse,
  type PathwayDetail,
  type PathwayEnrollment,
  type Permission,
  type ProgressSummary,
  type PromoValidateRequest,
  type PromoValidateResponse,
  type QuizCreateRequest,
  type QuizCreateResponse,
  type ReportDefinition,
  type ReportRun,
  type ResourceUploadRequest,
  type ResourceUploadResponse,
  type SSOConfigCreate,
  type SSOConfiguration,
  type SSODomainLookup,
  type ScormUploadRequest,
  type ScormUploadResponse,
  type SwitchCompanyRequest,
  type SwitchCompanyResponse,
  type Team,
  type TeamDetail,
  type TeamMembership,
  type UserList,
  type UserRole,
  type XblockCreateRequest,
  type XblockCreateResponse,
} from '../types';

export function useMisc(params?: { page?: number }) {
  return useQuery<PaginatedIndividualLearnerList, ApiError>({
    queryKey: ['misc', 'admin-individual-learners', 'list', params],
    queryFn: async () => {
      const response = await apiClient.get<PaginatedIndividualLearnerList>(
        '/admin/individual-learners/',
        { params }
      );
      return response.data;
    },
  });
}

export function useCreateMisc() {
  const qc = useQueryClient();
  return useMutation<IndividualLearner, ApiError, CreateIndividualLearner>({
    mutationFn: async (data) => {
      const response = await apiClient.post<IndividualLearner>('/admin/individual-learners/', data);
      return response.data;
    },
    onSuccess: () => {
      invalidateEntity(qc, 'misc');
    },
  });
}

export function useUpdateMisc() {
  const qc = useQueryClient();
  return useMutation<
    IndividualLearner,
    ApiError,
    { id: number; data: PatchedUpdateIndividualLearner }
  >({
    mutationFn: async ({ id, data }) => {
      const response = await apiClient.patch<IndividualLearner>(
        `/admin/individual-learners/${id}/`,
        data
      );
      return response.data;
    },
    onSuccess: (_data, { id }) => {
      invalidateEntity(qc, 'misc');
      invalidateEntityDetail(qc, 'misc', id);
    },
  });
}

export function useDeleteMisc() {
  const qc = useQueryClient();
  return useMutation<void, ApiError, number>({
    mutationFn: async (id: number) => {
      await apiClient.delete(`/admin/individual-learners/${id}/`);
    },
    onSuccess: () => {
      invalidateEntity(qc, 'misc');
    },
  });
}

export function useCreateMiscAdminIndividualLearnersBulkUploadCreate() {
  const qc = useQueryClient();
  return useMutation<BulkUploadResult, ApiError, BulkUpload>({
    mutationFn: async (data) => {
      const response = await apiClient.post<BulkUploadResult>(
        '/admin/individual-learners/bulk-upload/',
        data
      );
      return response.data;
    },
    onSuccess: () => {
      invalidateEntity(qc, 'misc');
    },
  });
}

export function useMiscSummary(params?: { page?: number }) {
  return useQuery<ProgressSummary, ApiError>({
    queryKey: ['misc', 'admin-lrs-progress-summary', 'summary'],
    queryFn: async () => {
      const response = await apiClient.get<ProgressSummary>('/admin/lrs/progress/summary/', {
        params,
      });
      return response.data;
    },
  });
}

export function useMiscCatalogCoursesRetrieve(params?: { page?: number }) {
  return useQuery<PaginatedCatalogCourseResponse, ApiError>({
    queryKey: ['misc', 'catalog-courses', 'list', params],
    queryFn: async () => {
      const response = await apiClient.get<PaginatedCatalogCourseResponse>('/catalog/courses/', {
        params,
      });
      return response.data;
    },
  });
}

export function useMiscCatalogCoursesDownloadRetrieve(params?: { page?: number }) {
  return useQuery<unknown, ApiError>({
    queryKey: ['misc', 'catalog-courses-download', 'list', params],
    queryFn: async () => {
      const response = await apiClient.get<unknown>('/catalog/courses/download/', { params });
      return response.data;
    },
  });
}

export function useMiscCertificatesDownloadRetrieve(certUuid: string) {
  return useQuery<unknown, ApiError>({
    queryKey: ['misc', certUuid, 'certificates-download', 'list'],
    queryFn: async () => {
      const response = await apiClient.get<unknown>(`/certificates/${certUuid}/download/`);
      return response.data;
    },
    enabled: !!certUuid,
  });
}

export function useMiscCertificatesPdfRetrieve(certUuid: string) {
  return useQuery<unknown, ApiError>({
    queryKey: ['misc', certUuid, 'certificates-pdf', 'list'],
    queryFn: async () => {
      const response = await apiClient.get<unknown>(`/certificates/${certUuid}/pdf/`);
      return response.data;
    },
    enabled: !!certUuid,
  });
}

export function useMiscCertificatesVerifyRetrieve(certUuid: string) {
  return useQuery<CertificateVerify, ApiError>({
    queryKey: ['misc', certUuid, 'certificates-verify', 'list'],
    queryFn: async () => {
      const response = await apiClient.get<CertificateVerify>(`/certificates/${certUuid}/verify/`);
      return response.data;
    },
    enabled: !!certUuid,
  });
}

export function useMiscCompaniesList(params?: { page?: number }) {
  return useQuery<PaginatedCompanyListList, ApiError>({
    queryKey: ['misc', 'companies', 'list', params],
    queryFn: async () => {
      const response = await apiClient.get<PaginatedCompanyListList>('/companies/', { params });
      return response.data;
    },
  });
}

export function useCreateMiscCompaniesCreate() {
  const qc = useQueryClient();
  return useMutation<CompanyDetail, ApiError, CompanyCreate>({
    mutationFn: async (data) => {
      const response = await apiClient.post<CompanyDetail>('/companies/', data);
      return response.data;
    },
    onSuccess: () => {
      invalidateEntity(qc, 'misc');
    },
  });
}

export function useMiscCompaniesRetrieve(slug: string) {
  return useQuery<CompanyDetail, ApiError>({
    queryKey: ['misc', 'companies', slug, 'detail'],
    queryFn: async () => {
      const response = await apiClient.get<CompanyDetail>(`/companies/${slug}/`);
      return response.data;
    },
    enabled: !!slug,
  });
}

export function useUpdateMiscCompaniesPartialUpdate() {
  const qc = useQueryClient();
  return useMutation<CompanyDetail, ApiError, { slug: string; data: PatchedCompanyUpdate }>({
    mutationFn: async ({ slug, data }) => {
      const response = await apiClient.patch<CompanyDetail>(`/companies/${slug}/`, data);
      return response.data;
    },
    onSuccess: () => {
      invalidateEntity(qc, 'misc');
    },
  });
}

export function useMiscCoursesPrerequisitesList(courseId: string) {
  return useQuery<CoursePrerequisite[], ApiError>({
    queryKey: ['misc', courseId, 'courses-prerequisites', 'list'],
    queryFn: async () => {
      const response = await apiClient.get<CoursePrerequisite[]>(
        `/courses/${courseId}/prerequisites/`
      );
      return response.data;
    },
    enabled: !!courseId,
  });
}

export function useMiscCoursesMetadataRetrieve(courseKey: string) {
  return useQuery<CourseMetadata, ApiError>({
    queryKey: ['misc', courseKey, 'courses-metadata', 'list'],
    queryFn: async () => {
      const response = await apiClient.get<CourseMetadata>(`/courses/${courseKey}/metadata/`);
      return response.data;
    },
    enabled: !!courseKey,
  });
}

export function useCreateMiscCoursesMetadataCreate() {
  const qc = useQueryClient();
  return useMutation<CourseMetadata, ApiError, { courseKey: string; data: CourseMetadata }>({
    mutationFn: async ({ courseKey, data }) => {
      const response = await apiClient.post<CourseMetadata>(
        `/courses/${courseKey}/metadata/`,
        data
      );
      return response.data;
    },
    onSuccess: () => {
      invalidateEntity(qc, 'misc');
    },
  });
}

export function useCreateMiscCoursesPurchaseCreate() {
  const qc = useQueryClient();
  return useMutation<CoursePurchaseResponse, ApiError, CoursePurchaseRequest>({
    mutationFn: async (data) => {
      const response = await apiClient.post<CoursePurchaseResponse>('/courses/purchase/', data);
      return response.data;
    },
    onSuccess: () => {
      invalidateEntity(qc, 'misc');
    },
  });
}

export function useMiscEnterprisesAllocationsList(
  enterpriseUuid: string,
  params?: { page?: number }
) {
  return useQuery<PaginatedCourseAllocationList, ApiError>({
    queryKey: ['misc', enterpriseUuid, 'enterprises-allocations', 'list', params],
    queryFn: async () => {
      const response = await apiClient.get<PaginatedCourseAllocationList>(
        `/enterprises/${enterpriseUuid}/allocations/`,
        { params }
      );
      return response.data;
    },
    enabled: !!enterpriseUuid,
  });
}

export function useCreateMiscEnterprisesAllocationsCreate() {
  const qc = useQueryClient();
  return useMutation<
    CourseAllocation,
    ApiError,
    { enterpriseUuid: string; data: AllocationCreate }
  >({
    mutationFn: async ({ enterpriseUuid, data }) => {
      const response = await apiClient.post<CourseAllocation>(
        `/enterprises/${enterpriseUuid}/allocations/`,
        data
      );
      return response.data;
    },
    onSuccess: () => {
      invalidateEntity(qc, 'misc');
    },
  });
}

export function useMiscEnterprisesAllocationsRetrieve(enterpriseUuid: string, uuid: string) {
  return useQuery<CourseAllocation, ApiError>({
    queryKey: ['misc', enterpriseUuid, 'enterprises-allocations', uuid, 'detail'],
    queryFn: async () => {
      const response = await apiClient.get<CourseAllocation>(
        `/enterprises/${enterpriseUuid}/allocations/${uuid}/`
      );
      return response.data;
    },
    enabled: !!enterpriseUuid && !!uuid,
  });
}

export function useMiscEnterprisesAllocationsEnrollmentStatusRetrieve(
  enterpriseUuid: string,
  uuid: string
) {
  return useQuery<EnrollmentStatusResponse, ApiError>({
    queryKey: ['misc', enterpriseUuid, uuid, 'enterprises-allocations-enrollment-status', 'list'],
    queryFn: async () => {
      const response = await apiClient.get<EnrollmentStatusResponse>(
        `/enterprises/${enterpriseUuid}/allocations/${uuid}/enrollment-status/`
      );
      return response.data;
    },
    enabled: !!enterpriseUuid && !!uuid,
  });
}

export function useCreateMiscEnterprisesAllocationsRevokeCreate() {
  const qc = useQueryClient();
  return useMutation<
    CourseAllocation,
    ApiError,
    { enterpriseUuid: string; uuid: string; data: Record<string, unknown> }
  >({
    mutationFn: async ({ enterpriseUuid, uuid, data }) => {
      const response = await apiClient.post<CourseAllocation>(
        `/enterprises/${enterpriseUuid}/allocations/${uuid}/revoke/`,
        data
      );
      return response.data;
    },
    onSuccess: () => {
      invalidateEntity(qc, 'misc');
    },
  });
}

export function useCreateMiscEnterprisesAllocationsAllocatePathwayCreate() {
  const qc = useQueryClient();
  return useMutation<
    PathwayAllocationResponse,
    ApiError,
    { enterpriseUuid: string; data: PathwayAllocation }
  >({
    mutationFn: async ({ enterpriseUuid, data }) => {
      const response = await apiClient.post<PathwayAllocationResponse>(
        `/enterprises/${enterpriseUuid}/allocations/allocate_pathway/`,
        data
      );
      return response.data;
    },
    onSuccess: () => {
      invalidateEntity(qc, 'misc');
    },
  });
}

export function useBulkMisc() {
  const qc = useQueryClient();
  return useMutation<
    BulkAllocationResponse,
    ApiError,
    { enterpriseUuid: string; data: BulkAllocation }
  >({
    mutationFn: async ({ enterpriseUuid, data }) => {
      const response = await apiClient.post<BulkAllocationResponse>(
        `/enterprises/${enterpriseUuid}/allocations/bulk_allocate/`,
        data
      );
      return response.data;
    },
    onSuccess: () => {
      invalidateEntity(qc, 'misc');
    },
  });
}

export function useMiscEnterprisesAnnouncementsList(
  enterpriseUuid: string,
  params?: { page?: number }
) {
  return useQuery<PaginatedAnnouncementListList, ApiError>({
    queryKey: ['misc', enterpriseUuid, 'enterprises-announcements', 'list', params],
    queryFn: async () => {
      const response = await apiClient.get<PaginatedAnnouncementListList>(
        `/enterprises/${enterpriseUuid}/announcements/`,
        { params }
      );
      return response.data;
    },
    enabled: !!enterpriseUuid,
  });
}

export function useCreateMiscEnterprisesAnnouncementsCreate() {
  const qc = useQueryClient();
  return useMutation<Announcement, ApiError, { enterpriseUuid: string; data: Announcement }>({
    mutationFn: async ({ enterpriseUuid, data }) => {
      const response = await apiClient.post<Announcement>(
        `/enterprises/${enterpriseUuid}/announcements/`,
        data
      );
      return response.data;
    },
    onSuccess: () => {
      invalidateEntity(qc, 'misc');
    },
  });
}

export function useMiscEnterprisesAnnouncementsRetrieve(enterpriseUuid: string, uuid: string) {
  return useQuery<Announcement, ApiError>({
    queryKey: ['misc', enterpriseUuid, 'enterprises-announcements', uuid, 'detail'],
    queryFn: async () => {
      const response = await apiClient.get<Announcement>(
        `/enterprises/${enterpriseUuid}/announcements/${uuid}/`
      );
      return response.data;
    },
    enabled: !!enterpriseUuid && !!uuid,
  });
}

export function useUpdateMiscEnterprisesAnnouncementsPartialUpdate() {
  const qc = useQueryClient();
  return useMutation<
    Announcement,
    ApiError,
    { enterpriseUuid: string; uuid: string; data: PatchedAnnouncement }
  >({
    mutationFn: async ({ enterpriseUuid, uuid, data }) => {
      const response = await apiClient.patch<Announcement>(
        `/enterprises/${enterpriseUuid}/announcements/${uuid}/`,
        data
      );
      return response.data;
    },
    onSuccess: () => {
      invalidateEntity(qc, 'misc');
    },
  });
}

export function useMiscEnterprisesAnnouncementsStatsRetrieve(enterpriseUuid: string, uuid: string) {
  return useQuery<AnnouncementStatsResponse, ApiError>({
    queryKey: ['misc', enterpriseUuid, uuid, 'enterprises-announcements-stats', 'list'],
    queryFn: async () => {
      const response = await apiClient.get<AnnouncementStatsResponse>(
        `/enterprises/${enterpriseUuid}/announcements/${uuid}/stats/`
      );
      return response.data;
    },
    enabled: !!enterpriseUuid && !!uuid,
  });
}

export function useUnreadMisc(enterpriseUuid: string, params?: { page?: number }) {
  return useQuery<PaginatedAnnouncementListList, ApiError>({
    queryKey: ['misc', enterpriseUuid, 'enterprises-announcements-unread', 'unread'],
    queryFn: async () => {
      const response = await apiClient.get<PaginatedAnnouncementListList>(
        `/enterprises/${enterpriseUuid}/announcements/unread/`,
        { params }
      );
      return response.data;
    },
    enabled: !!enterpriseUuid,
  });
}

export function useCreateMiscEnterprisesBulkAllocationsProcessCreate() {
  const qc = useQueryClient();
  return useMutation<unknown, ApiError, { enterpriseUuid: string; data: BulkCSVFile }>({
    mutationFn: async ({ enterpriseUuid, data }) => {
      const response = await apiClient.post<unknown>(
        `/enterprises/${enterpriseUuid}/bulk/allocations/process/`,
        data
      );
      return response.data;
    },
    onSuccess: () => {
      invalidateEntity(qc, 'misc');
    },
  });
}

export function useCreateMiscEnterprisesBulkAllocationsValidateCreate() {
  const qc = useQueryClient();
  return useMutation<
    BulkAllocationValidationResponse,
    ApiError,
    { enterpriseUuid: string; data: BulkCSVFile }
  >({
    mutationFn: async ({ enterpriseUuid, data }) => {
      const response = await apiClient.post<BulkAllocationValidationResponse>(
        `/enterprises/${enterpriseUuid}/bulk/allocations/validate/`,
        data
      );
      return response.data;
    },
    onSuccess: () => {
      invalidateEntity(qc, 'misc');
    },
  });
}

export function useCreateMiscEnterprisesBulkLearnersProcessCreate() {
  const qc = useQueryClient();
  return useMutation<unknown, ApiError, { enterpriseUuid: string; data: BulkCSVFile }>({
    mutationFn: async ({ enterpriseUuid, data }) => {
      const response = await apiClient.post<unknown>(
        `/enterprises/${enterpriseUuid}/bulk/learners/process/`,
        data
      );
      return response.data;
    },
    onSuccess: () => {
      invalidateEntity(qc, 'misc');
    },
  });
}

export function useCreateMiscEnterprisesBulkLearnersValidateCreate() {
  const qc = useQueryClient();
  return useMutation<
    BulkValidationResponse,
    ApiError,
    { enterpriseUuid: string; data: BulkCSVFile }
  >({
    mutationFn: async ({ enterpriseUuid, data }) => {
      const response = await apiClient.post<BulkValidationResponse>(
        `/enterprises/${enterpriseUuid}/bulk/learners/validate/`,
        data
      );
      return response.data;
    },
    onSuccess: () => {
      invalidateEntity(qc, 'misc');
    },
  });
}

export function useMiscEnterprisesBulkOperationsRetrieve(
  enterpriseUuid: string,
  operationUuid: string
) {
  return useQuery<BulkOperation, ApiError>({
    queryKey: ['misc', enterpriseUuid, 'enterprises-bulk-operations', operationUuid, 'detail'],
    queryFn: async () => {
      const response = await apiClient.get<BulkOperation>(
        `/enterprises/${enterpriseUuid}/bulk/operations/${operationUuid}/`
      );
      return response.data;
    },
    enabled: !!enterpriseUuid && !!operationUuid,
  });
}

export function useMiscEnterprisesCourseRequestsList(
  enterpriseUuid: string,
  params?: { page?: number }
) {
  return useQuery<PaginatedCourseRequestList, ApiError>({
    queryKey: ['misc', enterpriseUuid, 'enterprises-course-requests', 'list', params],
    queryFn: async () => {
      const response = await apiClient.get<PaginatedCourseRequestList>(
        `/enterprises/${enterpriseUuid}/course-requests/`,
        { params }
      );
      return response.data;
    },
    enabled: !!enterpriseUuid,
  });
}

export function useCreateMiscEnterprisesCourseRequestsCreate() {
  const qc = useQueryClient();
  return useMutation<
    CourseRequest,
    ApiError,
    { enterpriseUuid: string; data: CourseRequestCreate }
  >({
    mutationFn: async ({ enterpriseUuid, data }) => {
      const response = await apiClient.post<CourseRequest>(
        `/enterprises/${enterpriseUuid}/course-requests/`,
        data
      );
      return response.data;
    },
    onSuccess: () => {
      invalidateEntity(qc, 'misc');
    },
  });
}

export function useMiscEnterprisesCourseRequestsRetrieve(enterpriseUuid: string, uuid: string) {
  return useQuery<CourseRequest, ApiError>({
    queryKey: ['misc', enterpriseUuid, 'enterprises-course-requests', uuid, 'detail'],
    queryFn: async () => {
      const response = await apiClient.get<CourseRequest>(
        `/enterprises/${enterpriseUuid}/course-requests/${uuid}/`
      );
      return response.data;
    },
    enabled: !!enterpriseUuid && !!uuid,
  });
}

export function useCreateMiscEnterprisesCourseRequestsApproveCreate() {
  const qc = useQueryClient();
  return useMutation<
    CourseRequest,
    ApiError,
    { enterpriseUuid: string; uuid: string; data: Record<string, unknown> }
  >({
    mutationFn: async ({ enterpriseUuid, uuid, data }) => {
      const response = await apiClient.post<CourseRequest>(
        `/enterprises/${enterpriseUuid}/course-requests/${uuid}/approve/`,
        data
      );
      return response.data;
    },
    onSuccess: () => {
      invalidateEntity(qc, 'misc');
    },
  });
}

export function useCreateMiscEnterprisesCourseRequestsRejectCreate() {
  const qc = useQueryClient();
  return useMutation<
    CourseRequest,
    ApiError,
    { enterpriseUuid: string; uuid: string; data: CourseRequestReview }
  >({
    mutationFn: async ({ enterpriseUuid, uuid, data }) => {
      const response = await apiClient.post<CourseRequest>(
        `/enterprises/${enterpriseUuid}/course-requests/${uuid}/reject/`,
        data
      );
      return response.data;
    },
    onSuccess: () => {
      invalidateEntity(qc, 'misc');
    },
  });
}

export function useCreateMiscEnterprisesInviteUserCreate() {
  const qc = useQueryClient();
  return useMutation<UserList, ApiError, { enterpriseUuid: string; data: InviteUser }>({
    mutationFn: async ({ enterpriseUuid, data }) => {
      const response = await apiClient.post<UserList>(
        `/enterprises/${enterpriseUuid}/invite-user/`,
        data
      );
      return response.data;
    },
    onSuccess: () => {
      invalidateEntity(qc, 'misc');
    },
  });
}

export function useMiscEnterprisesLearnerStatsRetrieve(enterpriseUuid: string) {
  return useQuery<CompanyLearnerDashboardStats, ApiError>({
    queryKey: ['misc', enterpriseUuid, 'enterprises-learner-stats', 'list'],
    queryFn: async () => {
      const response = await apiClient.get<CompanyLearnerDashboardStats>(
        `/enterprises/${enterpriseUuid}/learner/stats/`
      );
      return response.data;
    },
    enabled: !!enterpriseUuid,
  });
}

export function useMiscEnterprisesLearnersList(enterpriseUuid: string, params?: { page?: number }) {
  return useQuery<PaginatedEnterpriseLearnerList, ApiError>({
    queryKey: ['misc', enterpriseUuid, 'enterprises-learners', 'list', params],
    queryFn: async () => {
      const response = await apiClient.get<PaginatedEnterpriseLearnerList>(
        `/enterprises/${enterpriseUuid}/learners/`,
        { params }
      );
      return response.data;
    },
    enabled: !!enterpriseUuid,
  });
}

export function useCreateMiscEnterprisesLearnersCreate() {
  const qc = useQueryClient();
  return useMutation<EnterpriseLearner, ApiError, { enterpriseUuid: string; data: CreateLearner }>({
    mutationFn: async ({ enterpriseUuid, data }) => {
      const response = await apiClient.post<EnterpriseLearner>(
        `/enterprises/${enterpriseUuid}/learners/`,
        data
      );
      return response.data;
    },
    onSuccess: () => {
      invalidateEntity(qc, 'misc');
    },
  });
}

export function useMiscEnterprisesLearnersRetrieve(enterpriseUuid: string, id: string) {
  return useQuery<EnterpriseLearnerDetail, ApiError>({
    queryKey: ['misc', enterpriseUuid, 'enterprises-learners', 'detail', id],
    queryFn: async () => {
      const response = await apiClient.get<EnterpriseLearnerDetail>(
        `/enterprises/${enterpriseUuid}/learners/${id}/`
      );
      return response.data;
    },
    enabled: !!enterpriseUuid && !!id,
  });
}

export function useUpdateMiscEnterprisesLearnersPartialUpdate() {
  const qc = useQueryClient();
  return useMutation<
    EnterpriseLearner,
    ApiError,
    { enterpriseUuid: string; id: string; data: PatchedUpdateLearner }
  >({
    mutationFn: async ({ enterpriseUuid, id, data }) => {
      const response = await apiClient.patch<EnterpriseLearner>(
        `/enterprises/${enterpriseUuid}/learners/${id}/`,
        data
      );
      return response.data;
    },
    onSuccess: (_data, { id }) => {
      invalidateEntity(qc, 'misc');
      invalidateEntityDetail(qc, 'misc', id);
    },
  });
}

export function useDeleteMiscEnterprisesLearnersDestroy() {
  const qc = useQueryClient();
  return useMutation<void, ApiError, { enterpriseUuid: string; id: string }>({
    mutationFn: async ({ enterpriseUuid, id }) => {
      await apiClient.delete(`/enterprises/${enterpriseUuid}/learners/${id}/`);
    },
    onSuccess: () => {
      invalidateEntity(qc, 'misc');
    },
  });
}

export function useCreateMiscEnterprisesLearnersResendWelcomeCreate() {
  const qc = useQueryClient();
  return useMutation<
    unknown,
    ApiError,
    { enterpriseUuid: string; id: string; data: EnterpriseLearner }
  >({
    mutationFn: async ({ enterpriseUuid, id, data }) => {
      const response = await apiClient.post<unknown>(
        `/enterprises/${enterpriseUuid}/learners/${id}/resend-welcome/`,
        data
      );
      return response.data;
    },
    onSuccess: (_data, { id }) => {
      invalidateEntity(qc, 'misc');
      invalidateEntityDetail(qc, 'misc', id);
    },
  });
}

export function useRestoreMisc() {
  const qc = useQueryClient();
  return useMutation<
    EnterpriseLearner,
    ApiError,
    { enterpriseUuid: string; id: string; data: EnterpriseLearner }
  >({
    mutationFn: async ({ enterpriseUuid, id, data }) => {
      const response = await apiClient.post<EnterpriseLearner>(
        `/enterprises/${enterpriseUuid}/learners/${id}/restore/`,
        data
      );
      return response.data;
    },
    onSuccess: (_data, { id }) => {
      invalidateEntity(qc, 'misc');
      invalidateEntityDetail(qc, 'misc', id);
    },
  });
}

export function useCreateMiscEnterprisesLearnersBulkUploadCreate() {
  const qc = useQueryClient();
  return useMutation<BulkUploadResult, ApiError, { enterpriseUuid: string; data: BulkUpload }>({
    mutationFn: async ({ enterpriseUuid, data }) => {
      const response = await apiClient.post<BulkUploadResult>(
        `/enterprises/${enterpriseUuid}/learners/bulk-upload/`,
        data
      );
      return response.data;
    },
    onSuccess: () => {
      invalidateEntity(qc, 'misc');
    },
  });
}

export function useMiscEnterprisesLrsEventsList(
  enterpriseUuid: string,
  params?: { page?: number }
) {
  return useQuery<PaginatedLearningEventList, ApiError>({
    queryKey: ['misc', enterpriseUuid, 'enterprises-lrs-events', 'list', params],
    queryFn: async () => {
      const response = await apiClient.get<PaginatedLearningEventList>(
        `/enterprises/${enterpriseUuid}/lrs/events/`,
        { params }
      );
      return response.data;
    },
    enabled: !!enterpriseUuid,
  });
}

export function useCreateMiscEnterprisesLrsEventsCreate() {
  const qc = useQueryClient();
  return useMutation<LearningEvent, ApiError, { enterpriseUuid: string; data: LearningEvent }>({
    mutationFn: async ({ enterpriseUuid, data }) => {
      const response = await apiClient.post<LearningEvent>(
        `/enterprises/${enterpriseUuid}/lrs/events/`,
        data
      );
      return response.data;
    },
    onSuccess: () => {
      invalidateEntity(qc, 'misc');
    },
  });
}

export function useMiscEnterprisesLrsEventsRetrieve(enterpriseUuid: string, id: number) {
  return useQuery<LearningEvent, ApiError>({
    queryKey: ['misc', enterpriseUuid, 'enterprises-lrs-events', 'detail', id],
    queryFn: async () => {
      const response = await apiClient.get<LearningEvent>(
        `/enterprises/${enterpriseUuid}/lrs/events/${id}/`
      );
      return response.data;
    },
    enabled: !!enterpriseUuid && !!id,
  });
}

export function useMiscEnterprisesLrsProgressList(
  enterpriseUuid: string,
  params?: { page?: number }
) {
  return useQuery<PaginatedLearnerProgressList, ApiError>({
    queryKey: ['misc', enterpriseUuid, 'enterprises-lrs-progress', 'list', params],
    queryFn: async () => {
      const response = await apiClient.get<PaginatedLearnerProgressList>(
        `/enterprises/${enterpriseUuid}/lrs/progress/`,
        { params }
      );
      return response.data;
    },
    enabled: !!enterpriseUuid,
  });
}

export function useMiscEnterprisesLrsProgressRetrieve(enterpriseUuid: string, id: number) {
  return useQuery<LearnerProgress, ApiError>({
    queryKey: ['misc', enterpriseUuid, 'enterprises-lrs-progress', 'detail', id],
    queryFn: async () => {
      const response = await apiClient.get<LearnerProgress>(
        `/enterprises/${enterpriseUuid}/lrs/progress/${id}/`
      );
      return response.data;
    },
    enabled: !!enterpriseUuid && !!id,
  });
}

export function useMiscSummaryEnterprisesLrsProgressSummaryRetrieve(enterpriseUuid: string) {
  return useQuery<ProgressSummary, ApiError>({
    queryKey: ['misc', enterpriseUuid, 'enterprises-lrs-progress-summary', 'summary'],
    queryFn: async () => {
      const response = await apiClient.get<ProgressSummary>(
        `/enterprises/${enterpriseUuid}/lrs/progress/summary/`
      );
      return response.data;
    },
    enabled: !!enterpriseUuid,
  });
}

export function useMiscEnterprisesMfaPolicyRetrieve(enterpriseUuid: string) {
  return useQuery<CompanyMFAPolicy, ApiError>({
    queryKey: ['misc', enterpriseUuid, 'enterprises-mfa-policy', 'list'],
    queryFn: async () => {
      const response = await apiClient.get<CompanyMFAPolicy>(
        `/enterprises/${enterpriseUuid}/mfa/policy/`
      );
      return response.data;
    },
    enabled: !!enterpriseUuid,
  });
}

export function useUpdateMiscEnterprisesMfaPolicyPartialUpdate() {
  const qc = useQueryClient();
  return useMutation<
    CompanyMFAPolicy,
    ApiError,
    { enterpriseUuid: string; data: PatchedCompanyMFAPolicy }
  >({
    mutationFn: async ({ enterpriseUuid, data }) => {
      const response = await apiClient.patch<CompanyMFAPolicy>(
        `/enterprises/${enterpriseUuid}/mfa/policy/`,
        data
      );
      return response.data;
    },
    onSuccess: () => {
      invalidateEntity(qc, 'misc');
    },
  });
}

export function useCreateMiscEnterprisesMfaUsersResetCreate() {
  const qc = useQueryClient();
  return useMutation<
    unknown,
    ApiError,
    { enterpriseUuid: string; userId: string; data: Record<string, unknown> }
  >({
    mutationFn: async ({ enterpriseUuid, userId, data }) => {
      const response = await apiClient.post<unknown>(
        `/enterprises/${enterpriseUuid}/mfa/users/${userId}/reset/`,
        data
      );
      return response.data;
    },
    onSuccess: () => {
      invalidateEntity(qc, 'misc');
    },
  });
}

export function useMiscEnterprisesNotesList(enterpriseUuid: string, params?: { page?: number }) {
  return useQuery<PaginatedLearnerNoteList, ApiError>({
    queryKey: ['misc', enterpriseUuid, 'enterprises-notes', 'list', params],
    queryFn: async () => {
      const response = await apiClient.get<PaginatedLearnerNoteList>(
        `/enterprises/${enterpriseUuid}/notes/`,
        { params }
      );
      return response.data;
    },
    enabled: !!enterpriseUuid,
  });
}

export function useCreateMiscEnterprisesNotesCreate() {
  const qc = useQueryClient();
  return useMutation<LearnerNote, ApiError, { enterpriseUuid: string; data: LearnerNote }>({
    mutationFn: async ({ enterpriseUuid, data }) => {
      const response = await apiClient.post<LearnerNote>(
        `/enterprises/${enterpriseUuid}/notes/`,
        data
      );
      return response.data;
    },
    onSuccess: () => {
      invalidateEntity(qc, 'misc');
    },
  });
}

export function useMiscEnterprisesNotesRetrieve(enterpriseUuid: string, uuid: string) {
  return useQuery<LearnerNote, ApiError>({
    queryKey: ['misc', enterpriseUuid, 'enterprises-notes', uuid, 'detail'],
    queryFn: async () => {
      const response = await apiClient.get<LearnerNote>(
        `/enterprises/${enterpriseUuid}/notes/${uuid}/`
      );
      return response.data;
    },
    enabled: !!enterpriseUuid && !!uuid,
  });
}

export function useUpdateMiscEnterprisesNotesPartialUpdate() {
  const qc = useQueryClient();
  return useMutation<
    LearnerNote,
    ApiError,
    { enterpriseUuid: string; uuid: string; data: PatchedLearnerNote }
  >({
    mutationFn: async ({ enterpriseUuid, uuid, data }) => {
      const response = await apiClient.patch<LearnerNote>(
        `/enterprises/${enterpriseUuid}/notes/${uuid}/`,
        data
      );
      return response.data;
    },
    onSuccess: () => {
      invalidateEntity(qc, 'misc');
    },
  });
}

export function useMiscEnterprisesPathwaysList(enterpriseUuid: string, params?: { page?: number }) {
  return useQuery<PaginatedPathwayList, ApiError>({
    queryKey: ['misc', enterpriseUuid, 'enterprises-pathways', 'list', params],
    queryFn: async () => {
      const response = await apiClient.get<PaginatedPathwayList>(
        `/enterprises/${enterpriseUuid}/pathways/`,
        { params }
      );
      return response.data;
    },
    enabled: !!enterpriseUuid,
  });
}

export function useCreateMiscEnterprisesPathwaysCreate() {
  const qc = useQueryClient();
  return useMutation<Pathway, ApiError, { enterpriseUuid: string; data: Pathway }>({
    mutationFn: async ({ enterpriseUuid, data }) => {
      const response = await apiClient.post<Pathway>(
        `/enterprises/${enterpriseUuid}/pathways/`,
        data
      );
      return response.data;
    },
    onSuccess: () => {
      invalidateEntity(qc, 'misc');
    },
  });
}

export function useMiscEnterprisesPathwaysEnrollmentsList(
  enterpriseUuid: string,
  pathwayPk: string,
  params?: { page?: number }
) {
  return useQuery<PaginatedPathwayEnrollmentList, ApiError>({
    queryKey: [
      'misc',
      enterpriseUuid,
      pathwayPk,
      'enterprises-pathways-enrollments',
      'list',
      params,
    ],
    queryFn: async () => {
      const response = await apiClient.get<PaginatedPathwayEnrollmentList>(
        `/enterprises/${enterpriseUuid}/pathways/${pathwayPk}/enrollments/`,
        { params }
      );
      return response.data;
    },
    enabled: !!enterpriseUuid && !!pathwayPk,
  });
}

export function useCreateMiscEnterprisesPathwaysEnrollmentsCreate() {
  const qc = useQueryClient();
  return useMutation<
    PathwayEnrollment,
    ApiError,
    { enterpriseUuid: string; pathwayPk: string; data: EnrollLearner }
  >({
    mutationFn: async ({ enterpriseUuid, pathwayPk, data }) => {
      const response = await apiClient.post<PathwayEnrollment>(
        `/enterprises/${enterpriseUuid}/pathways/${pathwayPk}/enrollments/`,
        data
      );
      return response.data;
    },
    onSuccess: () => {
      invalidateEntity(qc, 'misc');
    },
  });
}

export function useMiscEnterprisesPathwaysEnrollmentsRetrieve(
  enterpriseUuid: string,
  pathwayPk: string,
  id: number
) {
  return useQuery<PathwayEnrollment, ApiError>({
    queryKey: ['misc', enterpriseUuid, pathwayPk, 'enterprises-pathways-enrollments', 'detail', id],
    queryFn: async () => {
      const response = await apiClient.get<PathwayEnrollment>(
        `/enterprises/${enterpriseUuid}/pathways/${pathwayPk}/enrollments/${id}/`
      );
      return response.data;
    },
    enabled: !!enterpriseUuid && !!pathwayPk && !!id,
  });
}

export function useDeleteMiscEnterprisesPathwaysEnrollmentsDestroy() {
  const qc = useQueryClient();
  return useMutation<void, ApiError, { enterpriseUuid: string; pathwayPk: string; id: number }>({
    mutationFn: async ({ enterpriseUuid, pathwayPk, id }) => {
      await apiClient.delete(
        `/enterprises/${enterpriseUuid}/pathways/${pathwayPk}/enrollments/${id}/`
      );
    },
    onSuccess: () => {
      invalidateEntity(qc, 'misc');
    },
  });
}

export function useCreateMiscEnterprisesPathwaysEnrollmentsBulkEnrollCreate() {
  const qc = useQueryClient();
  return useMutation<
    BulkEnrollResponse,
    ApiError,
    { enterpriseUuid: string; pathwayPk: string; data: BulkEnroll }
  >({
    mutationFn: async ({ enterpriseUuid, pathwayPk, data }) => {
      const response = await apiClient.post<BulkEnrollResponse>(
        `/enterprises/${enterpriseUuid}/pathways/${pathwayPk}/enrollments/bulk_enroll/`,
        data
      );
      return response.data;
    },
    onSuccess: () => {
      invalidateEntity(qc, 'misc');
    },
  });
}

export function useMiscEnterprisesPathwaysRetrieve(enterpriseUuid: string, id: number) {
  return useQuery<PathwayDetail, ApiError>({
    queryKey: ['misc', enterpriseUuid, 'enterprises-pathways', 'detail', id],
    queryFn: async () => {
      const response = await apiClient.get<PathwayDetail>(
        `/enterprises/${enterpriseUuid}/pathways/${id}/`
      );
      return response.data;
    },
    enabled: !!enterpriseUuid && !!id,
  });
}

export function useUpdateMiscEnterprisesPathwaysPartialUpdate() {
  const qc = useQueryClient();
  return useMutation<
    Pathway,
    ApiError,
    { enterpriseUuid: string; id: number; data: PatchedPathway }
  >({
    mutationFn: async ({ enterpriseUuid, id, data }) => {
      const response = await apiClient.patch<Pathway>(
        `/enterprises/${enterpriseUuid}/pathways/${id}/`,
        data
      );
      return response.data;
    },
    onSuccess: (_data, { id }) => {
      invalidateEntity(qc, 'misc');
      invalidateEntityDetail(qc, 'misc', id);
    },
  });
}

export function useDeleteMiscEnterprisesPathwaysDestroy() {
  const qc = useQueryClient();
  return useMutation<void, ApiError, { enterpriseUuid: string; id: number }>({
    mutationFn: async ({ enterpriseUuid, id }) => {
      await apiClient.delete(`/enterprises/${enterpriseUuid}/pathways/${id}/`);
    },
    onSuccess: () => {
      invalidateEntity(qc, 'misc');
    },
  });
}

export function useMiscEnterprisesPathwaysCoursesRetrieve(
  enterpriseUuid: string,
  id: number,
  params?: { page?: number }
) {
  return useQuery<PaginatedPathwayCourseResponse, ApiError>({
    queryKey: ['misc', enterpriseUuid, 'enterprises-pathways-courses', 'detail', id],
    queryFn: async () => {
      const response = await apiClient.get<PaginatedPathwayCourseResponse>(
        `/enterprises/${enterpriseUuid}/pathways/${id}/courses/`,
        { params }
      );
      return response.data;
    },
    enabled: !!enterpriseUuid && !!id,
  });
}

export function useDeleteMiscEnterprisesPathwaysCoursesDestroy() {
  const qc = useQueryClient();
  return useMutation<void, ApiError, { coursePk: string; enterpriseUuid: string; id: number }>({
    mutationFn: async ({ coursePk, enterpriseUuid, id }) => {
      await apiClient.delete(`/enterprises/${enterpriseUuid}/pathways/${id}/courses/${coursePk}/`);
    },
    onSuccess: () => {
      invalidateEntity(qc, 'misc');
    },
  });
}

export function useUpdateMiscEnterprisesPathwaysCoursesUpdatePartialUpdate() {
  const qc = useQueryClient();
  return useMutation<
    PathwayCourse,
    ApiError,
    { coursePk: string; enterpriseUuid: string; id: number; data: PatchedPathwayCourseUpdate }
  >({
    mutationFn: async ({ coursePk, enterpriseUuid, id, data }) => {
      const response = await apiClient.patch<PathwayCourse>(
        `/enterprises/${enterpriseUuid}/pathways/${id}/courses/${coursePk}/update/`,
        data
      );
      return response.data;
    },
    onSuccess: (_data, { id }) => {
      invalidateEntity(qc, 'misc');
      invalidateEntityDetail(qc, 'misc', id);
    },
  });
}

export function useMiscEnterprisesReportsList(enterpriseUuid: string, params?: { page?: number }) {
  return useQuery<PaginatedReportDefinitionList, ApiError>({
    queryKey: ['misc', enterpriseUuid, 'enterprises-reports', 'list', params],
    queryFn: async () => {
      const response = await apiClient.get<PaginatedReportDefinitionList>(
        `/enterprises/${enterpriseUuid}/reports/`,
        { params }
      );
      return response.data;
    },
    enabled: !!enterpriseUuid,
  });
}

export function useCreateMiscEnterprisesReportsCreate() {
  const qc = useQueryClient();
  return useMutation<
    ReportDefinition,
    ApiError,
    { enterpriseUuid: string; data: ReportDefinition }
  >({
    mutationFn: async ({ enterpriseUuid, data }) => {
      const response = await apiClient.post<ReportDefinition>(
        `/enterprises/${enterpriseUuid}/reports/`,
        data
      );
      return response.data;
    },
    onSuccess: () => {
      invalidateEntity(qc, 'misc');
    },
  });
}

export function useMiscEnterprisesReportsRetrieve(enterpriseUuid: string, id: number) {
  return useQuery<ReportDefinition, ApiError>({
    queryKey: ['misc', enterpriseUuid, 'enterprises-reports', 'detail', id],
    queryFn: async () => {
      const response = await apiClient.get<ReportDefinition>(
        `/enterprises/${enterpriseUuid}/reports/${id}/`
      );
      return response.data;
    },
    enabled: !!enterpriseUuid && !!id,
  });
}

export function useUpdateMiscEnterprisesReportsPartialUpdate() {
  const qc = useQueryClient();
  return useMutation<
    ReportDefinition,
    ApiError,
    { enterpriseUuid: string; id: number; data: PatchedReportDefinition }
  >({
    mutationFn: async ({ enterpriseUuid, id, data }) => {
      const response = await apiClient.patch<ReportDefinition>(
        `/enterprises/${enterpriseUuid}/reports/${id}/`,
        data
      );
      return response.data;
    },
    onSuccess: (_data, { id }) => {
      invalidateEntity(qc, 'misc');
      invalidateEntityDetail(qc, 'misc', id);
    },
  });
}

export function useDeleteMiscEnterprisesReportsDestroy() {
  const qc = useQueryClient();
  return useMutation<void, ApiError, { enterpriseUuid: string; id: number }>({
    mutationFn: async ({ enterpriseUuid, id }) => {
      await apiClient.delete(`/enterprises/${enterpriseUuid}/reports/${id}/`);
    },
    onSuccess: () => {
      invalidateEntity(qc, 'misc');
    },
  });
}

export function useCreateMiscEnterprisesReportsGenerateCreate() {
  const qc = useQueryClient();
  return useMutation<unknown, ApiError, { enterpriseUuid: string; data: Record<string, unknown> }>({
    mutationFn: async ({ enterpriseUuid, data }) => {
      const response = await apiClient.post<unknown>(
        `/enterprises/${enterpriseUuid}/reports/generate/`,
        data
      );
      return response.data;
    },
    onSuccess: () => {
      invalidateEntity(qc, 'misc');
    },
  });
}

export function useMiscEnterprisesReportsRunsList(
  enterpriseUuid: string,
  params?: { page?: number }
) {
  return useQuery<PaginatedReportRunList, ApiError>({
    queryKey: ['misc', enterpriseUuid, 'enterprises-reports-runs', 'list', params],
    queryFn: async () => {
      const response = await apiClient.get<PaginatedReportRunList>(
        `/enterprises/${enterpriseUuid}/reports/runs/`,
        { params }
      );
      return response.data;
    },
    enabled: !!enterpriseUuid,
  });
}

export function useMiscEnterprisesReportsRunsRetrieve(enterpriseUuid: string, id: number) {
  return useQuery<ReportRun, ApiError>({
    queryKey: ['misc', enterpriseUuid, 'enterprises-reports-runs', 'detail', id],
    queryFn: async () => {
      const response = await apiClient.get<ReportRun>(
        `/enterprises/${enterpriseUuid}/reports/runs/${id}/`
      );
      return response.data;
    },
    enabled: !!enterpriseUuid && !!id,
  });
}

export function useMiscEnterprisesRolesList(enterpriseUuid: string, params?: { page?: number }) {
  return useQuery<PaginatedUserRoleList, ApiError>({
    queryKey: ['misc', enterpriseUuid, 'enterprises-roles', 'list', params],
    queryFn: async () => {
      const response = await apiClient.get<PaginatedUserRoleList>(
        `/enterprises/${enterpriseUuid}/roles/`,
        { params }
      );
      return response.data;
    },
    enabled: !!enterpriseUuid,
  });
}

export function useCreateMiscEnterprisesRolesCreate() {
  const qc = useQueryClient();
  return useMutation<UserRole, ApiError, { enterpriseUuid: string; data: UserRole }>({
    mutationFn: async ({ enterpriseUuid, data }) => {
      const response = await apiClient.post<UserRole>(
        `/enterprises/${enterpriseUuid}/roles/`,
        data
      );
      return response.data;
    },
    onSuccess: () => {
      invalidateEntity(qc, 'misc');
    },
  });
}

export function useMiscEnterprisesRolesRetrieve(
  enterpriseUuid: string,
  id: number,
  params?: { page?: number }
) {
  return useQuery<UserRole, ApiError>({
    queryKey: ['misc', enterpriseUuid, 'enterprises-roles', 'detail', id],
    queryFn: async () => {
      const response = await apiClient.get<UserRole>(
        `/enterprises/${enterpriseUuid}/roles/${id}/`,
        { params }
      );
      return response.data;
    },
    enabled: !!enterpriseUuid && !!id,
  });
}

export function useUpdateMiscEnterprisesRolesPartialUpdate() {
  const qc = useQueryClient();
  return useMutation<
    UserRole,
    ApiError,
    { enterpriseUuid: string; id: number; data: PatchedUserRole }
  >({
    mutationFn: async ({ enterpriseUuid, id, data }) => {
      const response = await apiClient.patch<UserRole>(
        `/enterprises/${enterpriseUuid}/roles/${id}/`,
        data
      );
      return response.data;
    },
    onSuccess: (_data, { id }) => {
      invalidateEntity(qc, 'misc');
      invalidateEntityDetail(qc, 'misc', id);
    },
  });
}

export function useDeleteMiscEnterprisesRolesDestroy() {
  const qc = useQueryClient();
  return useMutation<void, ApiError, { enterpriseUuid: string; id: number }>({
    mutationFn: async ({ enterpriseUuid, id }) => {
      await apiClient.delete(`/enterprises/${enterpriseUuid}/roles/${id}/`);
    },
    onSuccess: () => {
      invalidateEntity(qc, 'misc');
    },
  });
}

export function useUpdateMiscEnterprisesSlidesCoursesAdvancedSettingsPartialUpdate() {
  const qc = useQueryClient();
  return useMutation<
    unknown,
    ApiError,
    { courseKey: string; enterpriseUuid: string; data: Record<string, unknown> }
  >({
    mutationFn: async ({ courseKey, enterpriseUuid, data }) => {
      const response = await apiClient.patch<unknown>(
        `/enterprises/${enterpriseUuid}/slides/courses/${courseKey}/advanced_settings/`,
        data
      );
      return response.data;
    },
    onSuccess: () => {
      invalidateEntity(qc, 'misc');
    },
  });
}

export function useCreateMiscEnterprisesSlidesCoursesAssetsCreate() {
  const qc = useQueryClient();
  return useMutation<
    unknown,
    ApiError,
    { courseKey: string; enterpriseUuid: string; data: Record<string, unknown> }
  >({
    mutationFn: async ({ courseKey, enterpriseUuid, data }) => {
      const response = await apiClient.post<unknown>(
        `/enterprises/${enterpriseUuid}/slides/courses/${courseKey}/assets/`,
        data
      );
      return response.data;
    },
    onSuccess: () => {
      invalidateEntity(qc, 'misc');
    },
  });
}

export function useMiscEnterprisesSlidesCoursesIndexRetrieve(
  courseKey: string,
  enterpriseUuid: string
) {
  return useQuery<unknown, ApiError>({
    queryKey: ['misc', courseKey, enterpriseUuid, 'enterprises-slides-courses-index', 'list'],
    queryFn: async () => {
      const response = await apiClient.get<unknown>(
        `/enterprises/${enterpriseUuid}/slides/courses/${courseKey}/index/`
      );
      return response.data;
    },
    enabled: !!courseKey && !!enterpriseUuid,
  });
}

export function useCreateMiscEnterprisesSlidesCoursesStudioAssetsCreate() {
  const qc = useQueryClient();
  return useMutation<
    unknown,
    ApiError,
    { courseKey: string; enterpriseUuid: string; data: Record<string, unknown> }
  >({
    mutationFn: async ({ courseKey, enterpriseUuid, data }) => {
      const response = await apiClient.post<unknown>(
        `/enterprises/${enterpriseUuid}/slides/courses/${courseKey}/studio-assets/`,
        data
      );
      return response.data;
    },
    onSuccess: () => {
      invalidateEntity(qc, 'misc');
    },
  });
}

export function useMiscEnterprisesSlidesCoursesUnitsRetrieve(
  courseKey: string,
  enterpriseUuid: string
) {
  return useQuery<unknown, ApiError>({
    queryKey: ['misc', courseKey, enterpriseUuid, 'enterprises-slides-courses-units', 'list'],
    queryFn: async () => {
      const response = await apiClient.get<unknown>(
        `/enterprises/${enterpriseUuid}/slides/courses/${courseKey}/units/`
      );
      return response.data;
    },
    enabled: !!courseKey && !!enterpriseUuid,
  });
}

export function useCreateMiscEnterprisesSlidesCoursesUnitsCreate() {
  const qc = useQueryClient();
  return useMutation<
    unknown,
    ApiError,
    { courseKey: string; enterpriseUuid: string; data: Record<string, unknown> }
  >({
    mutationFn: async ({ courseKey, enterpriseUuid, data }) => {
      const response = await apiClient.post<unknown>(
        `/enterprises/${enterpriseUuid}/slides/courses/${courseKey}/units/`,
        data
      );
      return response.data;
    },
    onSuccess: () => {
      invalidateEntity(qc, 'misc');
    },
  });
}

export function useUpdateMiscEnterprisesSlidesCoursesUnitsPartialUpdate() {
  const qc = useQueryClient();
  return useMutation<
    unknown,
    ApiError,
    { courseKey: string; enterpriseUuid: string; unitId: string; data: Record<string, unknown> }
  >({
    mutationFn: async ({ courseKey, enterpriseUuid, unitId, data }) => {
      const response = await apiClient.patch<unknown>(
        `/enterprises/${enterpriseUuid}/slides/courses/${courseKey}/units/${unitId}/`,
        data
      );
      return response.data;
    },
    onSuccess: () => {
      invalidateEntity(qc, 'misc');
    },
  });
}

export function useMiscEnterprisesSlidesCoursesUnitsContentRetrieve(
  courseKey: string,
  enterpriseUuid: string,
  unitId: string
) {
  return useQuery<unknown, ApiError>({
    queryKey: [
      'misc',
      courseKey,
      enterpriseUuid,
      unitId,
      'enterprises-slides-courses-units-content',
      'list',
    ],
    queryFn: async () => {
      const response = await apiClient.get<unknown>(
        `/enterprises/${enterpriseUuid}/slides/courses/${courseKey}/units/${unitId}/content/`
      );
      return response.data;
    },
    enabled: !!courseKey && !!enterpriseUuid && !!unitId,
  });
}

export function useUpdateMiscEnterprisesSlidesCoursesUnitsContentPartialUpdate() {
  const qc = useQueryClient();
  return useMutation<
    unknown,
    ApiError,
    { courseKey: string; enterpriseUuid: string; unitId: string; data: Record<string, unknown> }
  >({
    mutationFn: async ({ courseKey, enterpriseUuid, unitId, data }) => {
      const response = await apiClient.patch<unknown>(
        `/enterprises/${enterpriseUuid}/slides/courses/${courseKey}/units/${unitId}/content/`,
        data
      );
      return response.data;
    },
    onSuccess: () => {
      invalidateEntity(qc, 'misc');
    },
  });
}

export function useCreateMiscEnterprisesSlidesCoursesUnitsDocCreate() {
  const qc = useQueryClient();
  return useMutation<
    unknown,
    ApiError,
    { courseKey: string; enterpriseUuid: string; unitId: string; data: Record<string, unknown> }
  >({
    mutationFn: async ({ courseKey, enterpriseUuid, unitId, data }) => {
      const response = await apiClient.post<unknown>(
        `/enterprises/${enterpriseUuid}/slides/courses/${courseKey}/units/${unitId}/doc/`,
        data
      );
      return response.data;
    },
    onSuccess: () => {
      invalidateEntity(qc, 'misc');
    },
  });
}

export function useMiscEnterprisesSlidesCoursesUnitsSlidesRetrieve(
  courseKey: string,
  enterpriseUuid: string,
  unitId: string
) {
  return useQuery<unknown, ApiError>({
    queryKey: [
      'misc',
      courseKey,
      enterpriseUuid,
      unitId,
      'enterprises-slides-courses-units-slides',
      'list',
    ],
    queryFn: async () => {
      const response = await apiClient.get<unknown>(
        `/enterprises/${enterpriseUuid}/slides/courses/${courseKey}/units/${unitId}/slides/`
      );
      return response.data;
    },
    enabled: !!courseKey && !!enterpriseUuid && !!unitId,
  });
}

export function useUpdateMiscEnterprisesSlidesCoursesUnitsSlidesPartialUpdate() {
  const qc = useQueryClient();
  return useMutation<
    unknown,
    ApiError,
    { courseKey: string; enterpriseUuid: string; unitId: string; data: Record<string, unknown> }
  >({
    mutationFn: async ({ courseKey, enterpriseUuid, unitId, data }) => {
      const response = await apiClient.patch<unknown>(
        `/enterprises/${enterpriseUuid}/slides/courses/${courseKey}/units/${unitId}/slides/`,
        data
      );
      return response.data;
    },
    onSuccess: () => {
      invalidateEntity(qc, 'misc');
    },
  });
}

export function useCreateMiscEnterprisesSlidesCoursesXblocksCreate() {
  const qc = useQueryClient();
  return useMutation<
    unknown,
    ApiError,
    { courseKey: string; enterpriseUuid: string; data: Record<string, unknown> }
  >({
    mutationFn: async ({ courseKey, enterpriseUuid, data }) => {
      const response = await apiClient.post<unknown>(
        `/enterprises/${enterpriseUuid}/slides/courses/${courseKey}/xblocks/`,
        data
      );
      return response.data;
    },
    onSuccess: () => {
      invalidateEntity(qc, 'misc');
    },
  });
}

export function useMiscEnterprisesSlidesCoursesXblocksRetrieve(
  blockLocator: string,
  courseKey: string,
  enterpriseUuid: string
) {
  return useQuery<unknown, ApiError>({
    queryKey: [
      'misc',
      courseKey,
      enterpriseUuid,
      'enterprises-slides-courses-xblocks',
      blockLocator,
      'detail',
    ],
    queryFn: async () => {
      const response = await apiClient.get<unknown>(
        `/enterprises/${enterpriseUuid}/slides/courses/${courseKey}/xblocks/${blockLocator}/`
      );
      return response.data;
    },
    enabled: !!blockLocator && !!courseKey && !!enterpriseUuid,
  });
}

export function useMiscEnterprisesSlidesCoursesXblocksChildrenRetrieve(
  blockLocator: string,
  courseKey: string,
  enterpriseUuid: string
) {
  return useQuery<unknown, ApiError>({
    queryKey: [
      'misc',
      blockLocator,
      courseKey,
      enterpriseUuid,
      'enterprises-slides-courses-xblocks-children',
      'list',
    ],
    queryFn: async () => {
      const response = await apiClient.get<unknown>(
        `/enterprises/${enterpriseUuid}/slides/courses/${courseKey}/xblocks/${blockLocator}/children/`
      );
      return response.data;
    },
    enabled: !!blockLocator && !!courseKey && !!enterpriseUuid,
  });
}

export function useMiscEnterprisesSlidesCoursesXblocksContainerPreviewRetrieve(
  blockLocator: string,
  courseKey: string,
  enterpriseUuid: string
) {
  return useQuery<unknown, ApiError>({
    queryKey: [
      'misc',
      blockLocator,
      courseKey,
      enterpriseUuid,
      'enterprises-slides-courses-xblocks-container_preview',
      'list',
    ],
    queryFn: async () => {
      const response = await apiClient.get<unknown>(
        `/enterprises/${enterpriseUuid}/slides/courses/${courseKey}/xblocks/${blockLocator}/container_preview/`
      );
      return response.data;
    },
    enabled: !!blockLocator && !!courseKey && !!enterpriseUuid,
  });
}

export function useCreateMiscEnterprisesSlidesCoursesXblocksContentCreate() {
  const qc = useQueryClient();
  return useMutation<
    unknown,
    ApiError,
    {
      blockLocator: string;
      courseKey: string;
      enterpriseUuid: string;
      data: Record<string, unknown>;
    }
  >({
    mutationFn: async ({ blockLocator, courseKey, enterpriseUuid, data }) => {
      const response = await apiClient.post<unknown>(
        `/enterprises/${enterpriseUuid}/slides/courses/${courseKey}/xblocks/${blockLocator}/content/`,
        data
      );
      return response.data;
    },
    onSuccess: () => {
      invalidateEntity(qc, 'misc');
    },
  });
}

export function useCreateMiscEnterprisesSlidesCoursesXblocksOraCreate() {
  const qc = useQueryClient();
  return useMutation<
    unknown,
    ApiError,
    {
      blockLocator: string;
      courseKey: string;
      enterpriseUuid: string;
      data: Record<string, unknown>;
    }
  >({
    mutationFn: async ({ blockLocator, courseKey, enterpriseUuid, data }) => {
      const response = await apiClient.post<unknown>(
        `/enterprises/${enterpriseUuid}/slides/courses/${courseKey}/xblocks/${blockLocator}/ora/`,
        data
      );
      return response.data;
    },
    onSuccess: () => {
      invalidateEntity(qc, 'misc');
    },
  });
}

export function useCreateMiscEnterprisesSlidesCoursesXblocksPublishCreate() {
  const qc = useQueryClient();
  return useMutation<
    unknown,
    ApiError,
    {
      blockLocator: string;
      courseKey: string;
      enterpriseUuid: string;
      data: Record<string, unknown>;
    }
  >({
    mutationFn: async ({ blockLocator, courseKey, enterpriseUuid, data }) => {
      const response = await apiClient.post<unknown>(
        `/enterprises/${enterpriseUuid}/slides/courses/${courseKey}/xblocks/${blockLocator}/publish/`,
        data
      );
      return response.data;
    },
    onSuccess: () => {
      invalidateEntity(qc, 'misc');
    },
  });
}

export function useCreateMiscEnterprisesSlidesCoursesXblocksScormCreate() {
  const qc = useQueryClient();
  return useMutation<
    unknown,
    ApiError,
    {
      blockLocator: string;
      courseKey: string;
      enterpriseUuid: string;
      data: Record<string, unknown>;
    }
  >({
    mutationFn: async ({ blockLocator, courseKey, enterpriseUuid, data }) => {
      const response = await apiClient.post<unknown>(
        `/enterprises/${enterpriseUuid}/slides/courses/${courseKey}/xblocks/${blockLocator}/scorm/`,
        data
      );
      return response.data;
    },
    onSuccess: () => {
      invalidateEntity(qc, 'misc');
    },
  });
}

export function useMiscEnterprisesSlidesTemplatesRetrieve(enterpriseUuid: string) {
  return useQuery<unknown, ApiError>({
    queryKey: ['misc', enterpriseUuid, 'enterprises-slides-templates', 'list'],
    queryFn: async () => {
      const response = await apiClient.get<unknown>(
        `/enterprises/${enterpriseUuid}/slides/templates/`
      );
      return response.data;
    },
    enabled: !!enterpriseUuid,
  });
}

export function useCreateMiscEnterprisesSlidesTemplatesCreate() {
  const qc = useQueryClient();
  return useMutation<unknown, ApiError, { enterpriseUuid: string; data: Record<string, unknown> }>({
    mutationFn: async ({ enterpriseUuid, data }) => {
      const response = await apiClient.post<unknown>(
        `/enterprises/${enterpriseUuid}/slides/templates/`,
        data
      );
      return response.data;
    },
    onSuccess: () => {
      invalidateEntity(qc, 'misc');
    },
  });
}

export function useMiscEnterprisesSsoList(enterpriseUuid: string) {
  return useQuery<SSOConfiguration[], ApiError>({
    queryKey: ['misc', enterpriseUuid, 'enterprises-sso', 'list'],
    queryFn: async () => {
      const response = await apiClient.get<SSOConfiguration[]>(
        `/enterprises/${enterpriseUuid}/sso/`
      );
      return response.data;
    },
    enabled: !!enterpriseUuid,
  });
}

export function useCreateMiscEnterprisesSsoCreate() {
  const qc = useQueryClient();
  return useMutation<SSOConfiguration, ApiError, { enterpriseUuid: string; data: SSOConfigCreate }>(
    {
      mutationFn: async ({ enterpriseUuid, data }) => {
        const response = await apiClient.post<SSOConfiguration>(
          `/enterprises/${enterpriseUuid}/sso/`,
          data
        );
        return response.data;
      },
      onSuccess: () => {
        invalidateEntity(qc, 'misc');
      },
    }
  );
}

export function useMiscEnterprisesSsoRetrieve(enterpriseUuid: string, id: string) {
  return useQuery<SSOConfiguration, ApiError>({
    queryKey: ['misc', enterpriseUuid, 'enterprises-sso', 'detail', id],
    queryFn: async () => {
      const response = await apiClient.get<SSOConfiguration>(
        `/enterprises/${enterpriseUuid}/sso/${id}/`
      );
      return response.data;
    },
    enabled: !!enterpriseUuid && !!id,
  });
}

export function useUpdateMiscEnterprisesSsoPartialUpdate() {
  const qc = useQueryClient();
  return useMutation<
    SSOConfiguration,
    ApiError,
    { enterpriseUuid: string; id: string; data: PatchedSSOConfigUpdate }
  >({
    mutationFn: async ({ enterpriseUuid, id, data }) => {
      const response = await apiClient.patch<SSOConfiguration>(
        `/enterprises/${enterpriseUuid}/sso/${id}/`,
        data
      );
      return response.data;
    },
    onSuccess: (_data, { id }) => {
      invalidateEntity(qc, 'misc');
      invalidateEntityDetail(qc, 'misc', id);
    },
  });
}

export function useDeleteMiscEnterprisesSsoDestroy() {
  const qc = useQueryClient();
  return useMutation<void, ApiError, { enterpriseUuid: string; id: string }>({
    mutationFn: async ({ enterpriseUuid, id }) => {
      await apiClient.delete(`/enterprises/${enterpriseUuid}/sso/${id}/`);
    },
    onSuccess: () => {
      invalidateEntity(qc, 'misc');
    },
  });
}

export function useMiscEnterprisesSubscriptionsCreditsBalanceRetrieve(enterpriseUuid: string) {
  return useQuery<unknown, ApiError>({
    queryKey: ['misc', enterpriseUuid, 'enterprises-subscriptions-credits-balance', 'list'],
    queryFn: async () => {
      const response = await apiClient.get<unknown>(
        `/enterprises/${enterpriseUuid}/subscriptions/credits/balance/`
      );
      return response.data;
    },
    enabled: !!enterpriseUuid,
  });
}

export function useMiscEnterprisesSubscriptionsCreditsTransactionsList(
  enterpriseUuid: string,
  params?: { page?: number }
) {
  return useQuery<PaginatedCreditTransactionList, ApiError>({
    queryKey: [
      'misc',
      enterpriseUuid,
      'enterprises-subscriptions-credits-transactions',
      'list',
      params,
    ],
    queryFn: async () => {
      const response = await apiClient.get<PaginatedCreditTransactionList>(
        `/enterprises/${enterpriseUuid}/subscriptions/credits/transactions/`,
        { params }
      );
      return response.data;
    },
    enabled: !!enterpriseUuid,
  });
}

export function useMiscEnterprisesSubscriptionsCreditsTransactionsRetrieve(
  enterpriseUuid: string,
  id: number
) {
  return useQuery<CreditTransaction, ApiError>({
    queryKey: [
      'misc',
      enterpriseUuid,
      'enterprises-subscriptions-credits-transactions',
      'detail',
      id,
    ],
    queryFn: async () => {
      const response = await apiClient.get<CreditTransaction>(
        `/enterprises/${enterpriseUuid}/subscriptions/credits/transactions/${id}/`
      );
      return response.data;
    },
    enabled: !!enterpriseUuid && !!id,
  });
}

export function useMiscEnterprisesSubscriptionsSubscriptionsList(
  enterpriseUuid: string,
  params?: { page?: number }
) {
  return useQuery<PaginatedCompanySubscriptionList, ApiError>({
    queryKey: ['misc', enterpriseUuid, 'enterprises-subscriptions-subscriptions', 'list', params],
    queryFn: async () => {
      const response = await apiClient.get<PaginatedCompanySubscriptionList>(
        `/enterprises/${enterpriseUuid}/subscriptions/subscriptions/`,
        { params }
      );
      return response.data;
    },
    enabled: !!enterpriseUuid,
  });
}

export function useMiscEnterprisesSubscriptionsSubscriptionsRetrieve(
  enterpriseUuid: string,
  id: number
) {
  return useQuery<CompanySubscription, ApiError>({
    queryKey: ['misc', enterpriseUuid, 'enterprises-subscriptions-subscriptions', 'detail', id],
    queryFn: async () => {
      const response = await apiClient.get<CompanySubscription>(
        `/enterprises/${enterpriseUuid}/subscriptions/subscriptions/${id}/`
      );
      return response.data;
    },
    enabled: !!enterpriseUuid && !!id,
  });
}

export function useMiscEnterprisesTeamsList(enterpriseUuid: string, params?: { page?: number }) {
  return useQuery<PaginatedTeamList, ApiError>({
    queryKey: ['misc', enterpriseUuid, 'enterprises-teams', 'list', params],
    queryFn: async () => {
      const response = await apiClient.get<PaginatedTeamList>(
        `/enterprises/${enterpriseUuid}/teams/`,
        { params }
      );
      return response.data;
    },
    enabled: !!enterpriseUuid,
  });
}

export function useCreateMiscEnterprisesTeamsCreate() {
  const qc = useQueryClient();
  return useMutation<Team, ApiError, { enterpriseUuid: string; data: Team }>({
    mutationFn: async ({ enterpriseUuid, data }) => {
      const response = await apiClient.post<Team>(`/enterprises/${enterpriseUuid}/teams/`, data);
      return response.data;
    },
    onSuccess: () => {
      invalidateEntity(qc, 'misc');
    },
  });
}

export function useMiscEnterprisesTeamsRetrieve(enterpriseUuid: string, id: string) {
  return useQuery<TeamDetail, ApiError>({
    queryKey: ['misc', enterpriseUuid, 'enterprises-teams', 'detail', id],
    queryFn: async () => {
      const response = await apiClient.get<TeamDetail>(
        `/enterprises/${enterpriseUuid}/teams/${id}/`
      );
      return response.data;
    },
    enabled: !!enterpriseUuid && !!id,
  });
}

export function useUpdateMiscEnterprisesTeamsPartialUpdate() {
  const qc = useQueryClient();
  return useMutation<Team, ApiError, { enterpriseUuid: string; id: string; data: PatchedTeam }>({
    mutationFn: async ({ enterpriseUuid, id, data }) => {
      const response = await apiClient.patch<Team>(
        `/enterprises/${enterpriseUuid}/teams/${id}/`,
        data
      );
      return response.data;
    },
    onSuccess: (_data, { id }) => {
      invalidateEntity(qc, 'misc');
      invalidateEntityDetail(qc, 'misc', id);
    },
  });
}

export function useDeleteMiscEnterprisesTeamsDestroy() {
  const qc = useQueryClient();
  return useMutation<void, ApiError, { enterpriseUuid: string; id: string }>({
    mutationFn: async ({ enterpriseUuid, id }) => {
      await apiClient.delete(`/enterprises/${enterpriseUuid}/teams/${id}/`);
    },
    onSuccess: () => {
      invalidateEntity(qc, 'misc');
    },
  });
}

export function useCreateMiscEnterprisesTeamsActivateCreate() {
  const qc = useQueryClient();
  return useMutation<Team, ApiError, { enterpriseUuid: string; id: string; data: Team }>({
    mutationFn: async ({ enterpriseUuid, id, data }) => {
      const response = await apiClient.post<Team>(
        `/enterprises/${enterpriseUuid}/teams/${id}/activate/`,
        data
      );
      return response.data;
    },
    onSuccess: (_data, { id }) => {
      invalidateEntity(qc, 'misc');
      invalidateEntityDetail(qc, 'misc', id);
    },
  });
}

export function useCreateMiscEnterprisesTeamsAssignPathwaysCreate() {
  const qc = useQueryClient();
  return useMutation<
    AssignPathwaysResponse,
    ApiError,
    { enterpriseUuid: string; id: string; data: AssignPathways }
  >({
    mutationFn: async ({ enterpriseUuid, id, data }) => {
      const response = await apiClient.post<AssignPathwaysResponse>(
        `/enterprises/${enterpriseUuid}/teams/${id}/assign_pathways/`,
        data
      );
      return response.data;
    },
    onSuccess: (_data, { id }) => {
      invalidateEntity(qc, 'misc');
      invalidateEntityDetail(qc, 'misc', id);
    },
  });
}

export function useCreateMiscEnterprisesTeamsDeactivateCreate() {
  const qc = useQueryClient();
  return useMutation<Team, ApiError, { enterpriseUuid: string; id: string; data: Team }>({
    mutationFn: async ({ enterpriseUuid, id, data }) => {
      const response = await apiClient.post<Team>(
        `/enterprises/${enterpriseUuid}/teams/${id}/deactivate/`,
        data
      );
      return response.data;
    },
    onSuccess: (_data, { id }) => {
      invalidateEntity(qc, 'misc');
      invalidateEntityDetail(qc, 'misc', id);
    },
  });
}

export function useCreateMiscEnterprisesTeamsMoveMemberCreate() {
  const qc = useQueryClient();
  return useMutation<
    TeamMembership,
    ApiError,
    { enterpriseUuid: string; id: string; data: MoveMember }
  >({
    mutationFn: async ({ enterpriseUuid, id, data }) => {
      const response = await apiClient.post<TeamMembership>(
        `/enterprises/${enterpriseUuid}/teams/${id}/move_member/`,
        data
      );
      return response.data;
    },
    onSuccess: (_data, { id }) => {
      invalidateEntity(qc, 'misc');
      invalidateEntityDetail(qc, 'misc', id);
    },
  });
}

export function useMiscEnterprisesUsersList(enterpriseUuid: string, params?: { page?: number }) {
  return useQuery<PaginatedEnterpriseUserList, ApiError>({
    queryKey: ['misc', enterpriseUuid, 'enterprises-users', 'list', params],
    queryFn: async () => {
      const response = await apiClient.get<PaginatedEnterpriseUserList>(
        `/enterprises/${enterpriseUuid}/users/`,
        { params }
      );
      return response.data;
    },
    enabled: !!enterpriseUuid,
  });
}

export function useMiscEnterprisesUsersRetrieve(enterpriseUuid: string, id: number) {
  return useQuery<EnterpriseUser, ApiError>({
    queryKey: ['misc', enterpriseUuid, 'enterprises-users', 'detail', id],
    queryFn: async () => {
      const response = await apiClient.get<EnterpriseUser>(
        `/enterprises/${enterpriseUuid}/users/${id}/`
      );
      return response.data;
    },
    enabled: !!enterpriseUuid && !!id,
  });
}

export function useUpdateMiscEnterprisesUsersPartialUpdate() {
  const qc = useQueryClient();
  return useMutation<
    EnterpriseUser,
    ApiError,
    { enterpriseUuid: string; id: number; data: PatchedEnterpriseUser }
  >({
    mutationFn: async ({ enterpriseUuid, id, data }) => {
      const response = await apiClient.patch<EnterpriseUser>(
        `/enterprises/${enterpriseUuid}/users/${id}/`,
        data
      );
      return response.data;
    },
    onSuccess: (_data, { id }) => {
      invalidateEntity(qc, 'misc');
      invalidateEntityDetail(qc, 'misc', id);
    },
  });
}

export function useDeleteMiscEnterprisesUsersDestroy() {
  const qc = useQueryClient();
  return useMutation<void, ApiError, { enterpriseUuid: string; id: number }>({
    mutationFn: async ({ enterpriseUuid, id }) => {
      await apiClient.delete(`/enterprises/${enterpriseUuid}/users/${id}/`);
    },
    onSuccess: () => {
      invalidateEntity(qc, 'misc');
    },
  });
}

export function useMiscLearnerCoursesRetrieve(params?: { page?: number }) {
  return useQuery<PaginatedLearnerCourseResponse, ApiError>({
    queryKey: ['misc', 'learner-courses', 'list', params],
    queryFn: async () => {
      const response = await apiClient.get<PaginatedLearnerCourseResponse>('/learner/courses/', {
        params,
      });
      return response.data;
    },
  });
}

export function useMiscLearnerNotesList(params?: { page?: number }) {
  return useQuery<PaginatedLearnerNoteList, ApiError>({
    queryKey: ['misc', 'learner-notes', 'list', params],
    queryFn: async () => {
      const response = await apiClient.get<PaginatedLearnerNoteList>('/learner/notes/', { params });
      return response.data;
    },
  });
}

export function useCreateMiscLearnerNotesCreate() {
  const qc = useQueryClient();
  return useMutation<LearnerNote, ApiError, LearnerNote>({
    mutationFn: async (data) => {
      const response = await apiClient.post<LearnerNote>('/learner/notes/', data);
      return response.data;
    },
    onSuccess: () => {
      invalidateEntity(qc, 'misc');
    },
  });
}

export function useMiscLearnerNotesRetrieve(uuid: string) {
  return useQuery<LearnerNote, ApiError>({
    queryKey: ['misc', 'learner-notes', uuid, 'detail'],
    queryFn: async () => {
      const response = await apiClient.get<LearnerNote>(`/learner/notes/${uuid}/`);
      return response.data;
    },
    enabled: !!uuid,
  });
}

export function useUpdateMiscLearnerNotesPartialUpdate() {
  const qc = useQueryClient();
  return useMutation<LearnerNote, ApiError, { uuid: string; data: PatchedLearnerNote }>({
    mutationFn: async ({ uuid, data }) => {
      const response = await apiClient.patch<LearnerNote>(`/learner/notes/${uuid}/`, data);
      return response.data;
    },
    onSuccess: () => {
      invalidateEntity(qc, 'misc');
    },
  });
}

export function useMiscLearnerStatsRetrieve() {
  return useQuery<LearnerDashboardStats, ApiError>({
    queryKey: ['misc', 'learner-stats', 'list'],
    queryFn: async () => {
      const response = await apiClient.get<LearnerDashboardStats>('/learner/stats/');
      return response.data;
    },
  });
}

export function useMiscLearnersList(params?: { page?: number }) {
  return useQuery<PaginatedEnterpriseLearnerList, ApiError>({
    queryKey: ['misc', 'learners', 'list', params],
    queryFn: async () => {
      const response = await apiClient.get<PaginatedEnterpriseLearnerList>('/learners/', {
        params,
      });
      return response.data;
    },
  });
}

export function useMiscMyPurchasesList() {
  return useQuery<IndividualPurchase[], ApiError>({
    queryKey: ['misc', 'my-purchases', 'list'],
    queryFn: async () => {
      const response = await apiClient.get<IndividualPurchase[]>('/my/purchases/');
      return response.data;
    },
  });
}

export function useMiscPermissionsList() {
  return useQuery<Permission[], ApiError>({
    queryKey: ['misc', 'permissions', 'list'],
    queryFn: async () => {
      const response = await apiClient.get<Permission[]>('/permissions/');
      return response.data;
    },
  });
}

export function useCreateMiscPromoValidateCreate() {
  const qc = useQueryClient();
  return useMutation<PromoValidateResponse, ApiError, PromoValidateRequest>({
    mutationFn: async (data) => {
      const response = await apiClient.post<PromoValidateResponse>('/promo/validate/', data);
      return response.data;
    },
    onSuccess: () => {
      invalidateEntity(qc, 'misc');
    },
  });
}

export function useMiscSectorsList(params?: { page?: number }) {
  return useQuery<PaginatedSectorList, ApiError>({
    queryKey: ['misc', 'sectors', 'list', params],
    queryFn: async () => {
      const response = await apiClient.get<PaginatedSectorList>('/sectors/', { params });
      return response.data;
    },
  });
}

export function useMiscSsoLookupRetrieve(params?: { page?: number }) {
  return useQuery<SSODomainLookup, ApiError>({
    queryKey: ['misc', 'sso-lookup', 'list', params],
    queryFn: async () => {
      const response = await apiClient.get<SSODomainLookup>('/sso/lookup/', { params });
      return response.data;
    },
  });
}

export function useMiscStudioRedirectRetrieve() {
  return useQuery<unknown, ApiError>({
    queryKey: ['misc', 'studio-redirect', 'list'],
    queryFn: async () => {
      const response = await apiClient.get<unknown>('/studio-redirect/');
      return response.data;
    },
  });
}

export function useCreateMiscStudioCoursesCreate() {
  const qc = useQueryClient();
  return useMutation<CourseCreateResponse, ApiError, CourseCreateRequest>({
    mutationFn: async (data) => {
      const response = await apiClient.post<CourseCreateResponse>('/studio/courses/', data);
      return response.data;
    },
    onSuccess: () => {
      invalidateEntity(qc, 'misc');
    },
  });
}

export function useMiscStudioCoursesRetrieve(courseKey: string) {
  return useQuery<CourseDetailResponse, ApiError>({
    queryKey: ['misc', 'studio-courses', courseKey, 'detail'],
    queryFn: async () => {
      const response = await apiClient.get<CourseDetailResponse>(`/studio/courses/${courseKey}/`);
      return response.data;
    },
    enabled: !!courseKey,
  });
}

export function useCreateMiscStudioCoursesDeactivateCreate() {
  const qc = useQueryClient();
  return useMutation<
    DeactivateCourseResponse,
    ApiError,
    { courseKey: string; data: Record<string, unknown> }
  >({
    mutationFn: async ({ courseKey, data }) => {
      const response = await apiClient.post<DeactivateCourseResponse>(
        `/studio/courses/${courseKey}/deactivate/`,
        data
      );
      return response.data;
    },
    onSuccess: () => {
      invalidateEntity(qc, 'misc');
    },
  });
}

export function useCreateMiscStudioCoursesHandbookCreate() {
  const qc = useQueryClient();
  return useMutation<
    HandbookUploadResponse,
    ApiError,
    { courseKey: string; data: HandbookUploadRequest }
  >({
    mutationFn: async ({ courseKey, data }) => {
      const response = await apiClient.post<HandbookUploadResponse>(
        `/studio/courses/${courseKey}/handbook/`,
        data
      );
      return response.data;
    },
    onSuccess: () => {
      invalidateEntity(qc, 'misc');
    },
  });
}

export function useCreateMiscStudioCoursesImageCreate() {
  const qc = useQueryClient();
  return useMutation<
    CourseImageUploadResponse,
    ApiError,
    { courseKey: string; data: CourseImageUploadRequest }
  >({
    mutationFn: async ({ courseKey, data }) => {
      const response = await apiClient.post<CourseImageUploadResponse>(
        `/studio/courses/${courseKey}/image/`,
        data
      );
      return response.data;
    },
    onSuccess: () => {
      invalidateEntity(qc, 'misc');
    },
  });
}

export function useCreateMiscStudioCoursesLogoCreate() {
  const qc = useQueryClient();
  return useMutation<
    CourseLogoUploadResponse,
    ApiError,
    { courseKey: string; data: CourseLogoUploadRequest }
  >({
    mutationFn: async ({ courseKey, data }) => {
      const response = await apiClient.post<CourseLogoUploadResponse>(
        `/studio/courses/${courseKey}/logo/`,
        data
      );
      return response.data;
    },
    onSuccess: () => {
      invalidateEntity(qc, 'misc');
    },
  });
}

export function useCreateMiscStudioCoursesLorCreate() {
  const qc = useQueryClient();
  return useMutation<LorCreateResponse, ApiError, { courseKey: string; data: LorCreateRequest }>({
    mutationFn: async ({ courseKey, data }) => {
      const response = await apiClient.post<LorCreateResponse>(
        `/studio/courses/${courseKey}/lor/`,
        data
      );
      return response.data;
    },
    onSuccess: () => {
      invalidateEntity(qc, 'misc');
    },
  });
}

export function useCreateMiscStudioCoursesQuizCreate() {
  const qc = useQueryClient();
  return useMutation<QuizCreateResponse, ApiError, { courseKey: string; data: QuizCreateRequest }>({
    mutationFn: async ({ courseKey, data }) => {
      const response = await apiClient.post<QuizCreateResponse>(
        `/studio/courses/${courseKey}/quiz/`,
        data
      );
      return response.data;
    },
    onSuccess: () => {
      invalidateEntity(qc, 'misc');
    },
  });
}

export function useCreateMiscStudioCoursesResourceCreate() {
  const qc = useQueryClient();
  return useMutation<
    ResourceUploadResponse,
    ApiError,
    { courseKey: string; data: ResourceUploadRequest }
  >({
    mutationFn: async ({ courseKey, data }) => {
      const response = await apiClient.post<ResourceUploadResponse>(
        `/studio/courses/${courseKey}/resource/`,
        data
      );
      return response.data;
    },
    onSuccess: () => {
      invalidateEntity(qc, 'misc');
    },
  });
}

export function useCreateMiscStudioCoursesXblockCreate() {
  const qc = useQueryClient();
  return useMutation<
    XblockCreateResponse,
    ApiError,
    { courseKey: string; data: XblockCreateRequest }
  >({
    mutationFn: async ({ courseKey, data }) => {
      const response = await apiClient.post<XblockCreateResponse>(
        `/studio/courses/${courseKey}/xblock/`,
        data
      );
      return response.data;
    },
    onSuccess: () => {
      invalidateEntity(qc, 'misc');
    },
  });
}

export function useCreateMiscStudioCoursesXblockUploadCreate() {
  const qc = useQueryClient();
  return useMutation<
    ScormUploadResponse,
    ApiError,
    { blockId: string; courseKey: string; data: ScormUploadRequest }
  >({
    mutationFn: async ({ blockId, courseKey, data }) => {
      const response = await apiClient.post<ScormUploadResponse>(
        `/studio/courses/${courseKey}/xblock/${blockId}/upload/`,
        data
      );
      return response.data;
    },
    onSuccess: () => {
      invalidateEntity(qc, 'misc');
    },
  });
}

export function useCreateMiscStudioValidateOrgCreate() {
  const qc = useQueryClient();
  return useMutation<unknown, ApiError, Record<string, unknown>>({
    mutationFn: async (data) => {
      const response = await apiClient.post<unknown>('/studio/validate-org/', data);
      return response.data;
    },
    onSuccess: () => {
      invalidateEntity(qc, 'misc');
    },
  });
}

export function useMiscSwitchCompanyRetrieve() {
  return useQuery<SwitchCompanyResponse, ApiError>({
    queryKey: ['misc', 'switch-company', 'list'],
    queryFn: async () => {
      const response = await apiClient.get<SwitchCompanyResponse>('/switch-company/');
      return response.data;
    },
  });
}

export function useCreateMiscSwitchCompanyCreate() {
  const qc = useQueryClient();
  return useMutation<SwitchCompanyResponse, ApiError, SwitchCompanyRequest>({
    mutationFn: async (data) => {
      const response = await apiClient.post<SwitchCompanyResponse>('/switch-company/', data);
      return response.data;
    },
    onSuccess: () => {
      invalidateEntity(qc, 'misc');
    },
  });
}

export function useMiscUsersList(params?: { page?: number }) {
  return useQuery<PaginatedUserListList, ApiError>({
    queryKey: ['misc', 'users', 'list', params],
    queryFn: async () => {
      const response = await apiClient.get<PaginatedUserListList>('/users/', { params });
      return response.data;
    },
  });
}

export function useMiscUsersRetrieve(id: number) {
  return useQuery<UserList, ApiError>({
    queryKey: ['misc', 'users', 'detail', id],
    queryFn: async () => {
      const response = await apiClient.get<UserList>(`/users/${id}/`);
      return response.data;
    },
    enabled: !!id,
  });
}

export function useMyMisc() {
  const qc = useQueryClient();
  return useMutation<ChangePasswordSuccess, ApiError, ChangePassword>({
    mutationFn: async (data) => {
      const response = await apiClient.post<ChangePasswordSuccess>(
        '/users/profiles/me/change-password/',
        data
      );
      return response.data;
    },
    onSuccess: () => {
      invalidateEntity(qc, 'misc');
    },
  });
}

export function useCreateMiscWebhooksStripeCreate() {
  const qc = useQueryClient();
  return useMutation<unknown, ApiError, Record<string, unknown>>({
    mutationFn: async (data) => {
      const response = await apiClient.post<unknown>('/webhooks/stripe/', data);
      return response.data;
    },
    onSuccess: () => {
      invalidateEntity(qc, 'misc');
    },
  });
}
